import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatError } from "@/lib/api";
import { PageHeader, ScopeNotice, useScopeGuard } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["admin", "calidad", "operario", "consulta"];

export default function Usuarios() {
  const { user, scopeTenant } = useAuth();
  const needsScope = useScopeGuard(user, scopeTenant);
  const [usuarios, setUsuarios] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nombre: "", email: "", password: "", rol: "calidad" });

  const load = useCallback(async () => {
    if (needsScope) return;
    const { data } = await api.get("/usuarios");
    setUsuarios(data);
  }, [needsScope]);
  useEffect(() => { load(); }, [load]);

  const crear = async () => {
    if (!form.email || !form.password || !form.nombre) return toast.error("Todos los campos son obligatorios");
    try {
      await api.post("/usuarios", form);
      toast.success("Usuario creado");
      setOpen(false);
      setForm({ nombre: "", email: "", password: "", rol: "calidad" });
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const toggleActivo = async (u) => {
    try {
      await api.patch(`/usuarios/${u.id}`, { activo: !u.activo });
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  if (needsScope) return <ScopeNotice />;

  return (
    <div data-testid="usuarios-page">
      <PageHeader overline="Configuración" title="Usuarios"
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="nuevo-usuario-btn" className="bg-[#FF5625] hover:bg-[#E0451A]"><Plus size={16} className="mr-1" /> Nuevo usuario</Button>
            </DialogTrigger>
            <DialogContent className="bg-white" data-testid="usuario-dialog">
              <DialogHeader><DialogTitle className="text-[#0E2A47]">Crear usuario</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Nombre</Label>
                  <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="usuario-nombre" className="mt-1.5" /></div>
                <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="usuario-email" className="mt-1.5" /></div>
                <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Contraseña</Label>
                  <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="usuario-password" className="mt-1.5" /></div>
                <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Rol</Label>
                  <Select value={form.rol} onValueChange={(v) => setForm({ ...form, rol: v })}>
                    <SelectTrigger data-testid="usuario-rol" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              <DialogFooter><Button onClick={crear} data-testid="usuario-submit" className="bg-[#0E2A47] hover:bg-[#163C65]">Crear</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        } />

      <Card className="border-[#E2E8F0] overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-[#F7F8FA]">
            <TableHead>Nombre</TableHead><TableHead>Email</TableHead><TableHead>Rol</TableHead><TableHead>Activo</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {usuarios.map((u) => (
              <TableRow key={u.id} data-testid={`usuario-row-${u.id}`} className="hover:bg-[#F7F8FA]">
                <TableCell className="font-medium text-[#0E2A47]">{u.nombre}</TableCell>
                <TableCell className="text-[#0E2A47]/70">{u.email}</TableCell>
                <TableCell><Badge variant="outline" className="capitalize border-[#E2E8F0]">{u.rol}</Badge></TableCell>
                <TableCell><Switch checked={u.activo} onCheckedChange={() => toggleActivo(u)} data-testid={`usuario-activo-${u.id}`} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
